import { useApp, useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";

import { APPLICATIONS_ID } from "@/constants";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import { useLocalizedUrl } from "@/hooks/useLocalizedUrl";
import useTranslatedMetaInfo from "@/hooks/useTranslatedMetaInfo";
import { replaceHelmetTokens } from "@/utils";
import { getMetaDataForJsonLD } from "@/utils/getMetaDataForJsonLD";

import PageMetaTags from "./PageMetaTags";
import { PageStaticMetaTagsProps } from "./types";

function buildJsonLd(rawJsonLd: unknown, tokens: Record<string, string | undefined>): Record<string, unknown> | null {
  if (!rawJsonLd) return null;
  try {
    const jsonSafeTokens = Object.fromEntries(Object.entries(tokens).map(([key, value]) => [key, JSON.stringify(value ?? "").slice(1, -1)]));
    const populated = replaceHelmetTokens(JSON.stringify(rawJsonLd), jsonSafeTokens as unknown as string);
    return populated ? JSON.parse(populated) : null;
  } catch {
    return null;
  }
}

const PageStaticMetaTags = ({
  pageId,
  properties,
  ogImage,
  canonicalUrl,
  jsonLdTokens,
  "data-testid": testId = "PageStaticMetaTags",
  prerenderStatusCode,
}: PageStaticMetaTagsProps) => {
  const { t } = useTranslation();
  const { getTranslatedMetaInfo } = useTranslatedMetaInfo();
  const { applicationName } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const localizedUrl = useLocalizedUrl();
  const localizePathname = useLocalizedPathname();

  const dynamicValues = { ...properties, applicationName };

  const { title, description } = getTranslatedMetaInfo(pageId, dynamicValues);

  const pageMetaData = getMetaDataForJsonLD(applicationId as APPLICATIONS_ID);
  const rawJsonLd = pageMetaData?.[pageId as keyof typeof pageMetaData];
  const jsonLd = buildJsonLd(rawJsonLd, {
    applicationName,
    webPageDescription: description,
    webPageUrl: localizedUrl(),
    webPageImage: ogImage ?? "",
    webPageCreditText: applicationName,
    homeLabel: t("meta:jsonLd.home.label"),
    homeUrl: `${window.location.origin}${localizePathname("/")}`,
    ...jsonLdTokens,
  });

  return (
    <PageMetaTags
      title={title}
      description={description}
      ogImage={ogImage}
      canonicalUrl={canonicalUrl}
      prerenderStatusCode={prerenderStatusCode}
      data-testid={testId}
      jsonLd={jsonLd}
    />
  );
};

export default PageStaticMetaTags;
